/**
 * Absolute Image Element Component
 * Draggable, resizable image with absolute positioning
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Trash2, Lock, Unlock } from 'lucide-react';
import type { AbsoluteElement } from '../../types/template.types';
import { AbsoluteElementContextMenu } from './absolute-element-context-menu';
import { useTranslation } from 'react-i18next';

interface AbsoluteImageElementProps {
  element: AbsoluteElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (element: AbsoluteElement) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onCut: () => void;
  onCopy: () => void;
  onPaste: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
  onBringForward: () => void;
  onSendBackward: () => void;
  canPaste?: boolean;
  gridSize?: number; // For snapping
}

export function AbsoluteImageElement({
  element,
  isSelected,
  onSelect,
  onChange,
  onDelete,
  onDuplicate,
  onCut,
  onCopy,
  onPaste,
  onBringToFront,
  onSendToBack,
  onBringForward,
  onSendBackward,
  canPaste = false,
  gridSize,
}: AbsoluteImageElementProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const dragStartPos = useRef({ x: 0, y: 0, elementX: 0, elementY: 0 });
  const resizeStartState = useRef({ x: 0, y: 0, width: 0, height: 0, elementX: 0, elementY: 0 });
  const hasMoved = useRef(false);

  const snapToGrid = useCallback((value: number): number => {
    if (!gridSize) return Math.round(value);
    return Math.round(value / gridSize) * gridSize;
  }, [gridSize]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only left click
    if (element.locked) {
      // If locked, only select, don't start drag
      e.stopPropagation();
      onSelect();
      return;
    }
    
    e.stopPropagation();
    
    // Always select on mouse down
    onSelect();
    
    // Start potential drag (only becomes real drag if mouse moves)
    hasMoved.current = false;
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      elementX: element.x,
      elementY: element.y,
    };
    setIsDragging(true);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDragging(false);
    hasMoved.current = false;
  };

  const handleResizeMouseDown = (e: React.MouseEvent, handle: string) => {
    if (element.locked) return; // Don't resize if locked
    
    e.stopPropagation();
    e.preventDefault();
    
    onSelect();
    setIsResizing(true);
    setResizeHandle(handle);
    resizeStartState.current = {
      x: e.clientX,
      y: e.clientY,
      width: element.width,
      height: element.height,
      elementX: element.x,
      elementY: element.y,
    };
  };

  const handleResizeMouseUp = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(false);
    setResizeHandle(null);
  };

  const handleResizeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStartPos.current.x;
        const deltaY = e.clientY - dragStartPos.current.y;
        
        // Only actually move if moved more than 3px (prevents accidental moves on click)
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          hasMoved.current = true;

          const newX = snapToGrid(dragStartPos.current.elementX + deltaX);
          const newY = snapToGrid(dragStartPos.current.elementY + deltaY);

          onChange({
            ...element,
            x: Math.max(0, newX),
            y: Math.max(0, newY),
          });
        }
      } else if (isResizing && resizeHandle) {
        const deltaX = e.clientX - resizeStartState.current.x;
        const deltaY = e.clientY - resizeStartState.current.y;

        let newWidth = resizeStartState.current.width;
        let newHeight = resizeStartState.current.height;
        let newX = resizeStartState.current.elementX;
        let newY = resizeStartState.current.elementY;

        // Aspect ratio lock
        if (element.aspectRatioLocked && element.originalWidth && element.originalHeight) {
          const ratio = element.originalWidth / element.originalHeight;
          
          // For corner handles, use the larger delta to maintain aspect ratio
          if (resizeHandle.length === 2) {
            // Use whichever dimension changed more
            const absDeltaX = Math.abs(deltaX);
            const absDeltaY = Math.abs(deltaY);
            
            if (absDeltaX > absDeltaY) {
              // Width is the dominant change
              if (resizeHandle.includes('e')) {
                newWidth = Math.max(50, resizeStartState.current.width + deltaX);
              } else if (resizeHandle.includes('w')) {
                newWidth = Math.max(50, resizeStartState.current.width - deltaX);
              }
              newHeight = newWidth / ratio;
              
              // Adjust Y position for north handles
              if (resizeHandle.includes('n')) {
                newY = resizeStartState.current.elementY + (resizeStartState.current.height - newHeight);
              }
              
              // Adjust X position for west handles
              if (resizeHandle.includes('w')) {
                newX = resizeStartState.current.elementX + (resizeStartState.current.width - newWidth);
              }
            } else {
              // Height is the dominant change
              if (resizeHandle.includes('s')) {
                newHeight = Math.max(50, resizeStartState.current.height + deltaY);
              } else if (resizeHandle.includes('n')) {
                newHeight = Math.max(50, resizeStartState.current.height - deltaY);
              }
              newWidth = newHeight * ratio;
              
              // Adjust Y position for north handles
              if (resizeHandle.includes('n')) {
                newY = resizeStartState.current.elementY + (resizeStartState.current.height - newHeight);
              }
              
              // Adjust X position for west handles
              if (resizeHandle.includes('w')) {
                newX = resizeStartState.current.elementX + (resizeStartState.current.width - newWidth);
              }
            }
          } else {
            // Edge handles
            if (resizeHandle === 'e') {
              newWidth = Math.max(50, resizeStartState.current.width + deltaX);
              newHeight = newWidth / ratio;
            } else if (resizeHandle === 'w') {
              newWidth = Math.max(50, resizeStartState.current.width - deltaX);
              newHeight = newWidth / ratio;
              newX = resizeStartState.current.elementX + (resizeStartState.current.width - newWidth);
            } else if (resizeHandle === 's') {
              newHeight = Math.max(50, resizeStartState.current.height + deltaY);
              newWidth = newHeight * ratio;
            } else if (resizeHandle === 'n') {
              newHeight = Math.max(50, resizeStartState.current.height - deltaY);
              newWidth = newHeight * ratio;
              newY = resizeStartState.current.elementY + (resizeStartState.current.height - newHeight);
            }
          }
        } else {
          // No aspect ratio lock - standard resize
          if (resizeHandle.includes('e')) {
            newWidth = Math.max(50, resizeStartState.current.width + deltaX);
          }
          if (resizeHandle.includes('w')) {
            newWidth = Math.max(50, resizeStartState.current.width - deltaX);
            newX = resizeStartState.current.elementX + (resizeStartState.current.width - newWidth);
          }
          if (resizeHandle.includes('s')) {
            newHeight = Math.max(50, resizeStartState.current.height + deltaY);
          }
          if (resizeHandle.includes('n')) {
            newHeight = Math.max(50, resizeStartState.current.height - deltaY);
            newY = resizeStartState.current.elementY + (resizeStartState.current.height - newHeight);
          }
        }

        onChange({
          ...element,
          x: snapToGrid(Math.max(0, newX)),
          y: snapToGrid(Math.max(0, newY)),
          width: snapToGrid(newWidth),
          height: snapToGrid(newHeight),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
      hasMoved.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, resizeHandle, element, onChange, snapToGrid]);

  return (
    <AbsoluteElementContextMenu
      onCut={onCut}
      onCopy={onCopy}
      onPaste={onPaste}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onBringToFront={onBringToFront}
      onSendToBack={onSendToBack}
      onBringForward={onBringForward}
      onSendBackward={onSendBackward}
      canPaste={canPaste}
    >
      <div
        className={`absolute ${element.locked ? 'cursor-not-allowed' : 'cursor-move'} ${
          isSelected 
            ? element.locked ? 'ring-2 ring-destructive' : 'ring-2 ring-primary'
            : 'hover:ring-1 hover:ring-primary'
        }`}
        style={{
          left: element.x,
          top: element.y,
          width: element.width,
          height: element.height,
          zIndex: isSelected ? 9998 : (element.zIndex || 500), // Default 500 (same as header/root), selected always on top
          pointerEvents: isSelected ? 'auto' : 'auto', // Always catch clicks, but background is transparent
        }}
        onMouseDown={handleMouseDown}
        onClick={(e) => {
          e.stopPropagation();
          // If not selected, select it
          if (!isSelected) {
            onSelect();
          }
        }}
        onMouseUp={handleMouseUp}
      >
      {/* Image or placeholder */}
      {element.imageUrl ? (
        <img
          src={element.imageUrl}
          alt="Absolute positioned"
          className="w-full h-full object-fill pointer-events-none"
          draggable={false}
        />
      ) : (
        <div className="w-full h-full border-2 border-dashed border-muted-foreground bg-muted flex items-center justify-center text-muted-foreground text-sm">
          {t('invoiceTemplate.elementProperties.uploadImage')}
        </div>
      )}

      {/* Action buttons */}
      {isSelected && (
        <>
          {/* Lock/Unlock button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChange({ ...element, locked: !element.locked });
            }}
            className={`absolute -top-2 -right-10 w-6 h-6 rounded-full text-primary-foreground flex items-center justify-center shadow-lg z-10 ${
              element.locked ? 'bg-destructive hover:bg-destructive/90' : 'bg-secondary hover:bg-secondary/80'
            }`}
            title={element.locked ? t('invoiceTemplate.elementProperties.unlocked') : t('invoiceTemplate.elementProperties.locked')}
          >
            {element.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
          </button>
          
          {/* Delete button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 shadow-lg z-10"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </>
      )}

      {/* Resize handles - only show if not locked */}
      {isSelected && !element.locked && (
        <>
          {/* Corners */}
          <div
            className="absolute -top-1 -left-1 w-3 h-3 bg-primary border border-background cursor-nw-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />
          <div
            className="absolute -top-1 -right-1 w-3 h-3 bg-primary border border-background cursor-ne-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />
          <div
            className="absolute -bottom-1 -left-1 w-3 h-3 bg-primary border border-background cursor-sw-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />
          <div
            className="absolute -bottom-1 -right-1 w-3 h-3 bg-primary border border-background cursor-se-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />

          {/* Edges */}
          <div
            className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary border border-background cursor-n-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 'n')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />
          <div
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-primary border border-background cursor-s-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 's')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -left-1 w-3 h-3 bg-primary border border-background cursor-w-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 'w')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -right-1 w-3 h-3 bg-primary border border-background cursor-e-resize"
            onMouseDown={(e) => handleResizeMouseDown(e, 'e')}
            onMouseUp={handleResizeMouseUp}
            onClick={handleResizeClick}
          />
        </>
      )}
      </div>
    </AbsoluteElementContextMenu>
  );
}
